import React, { useEffect, useState } from 'react';
import styles from './CommunityImageCard.module.scss';
import { CommunityImageCardFragment } from '@src/graphql/gql-types';
import MediaImage from '../MediaImage';
import Heading from '@src/elements/Heading';
import GalleryModal from '../GalleryModal';
import Modal from 'react-modal';
import {
  ModalState,
  useModalOpenOrClose
} from '@src/hooks/useModalOpenOrClose';
import { useWindow } from '@src/hooks/useWindow';
import { useRouter } from 'next/router';
import { CloudinarySource } from '@src/types/cloudinary';
import BoxIcon from '@src/elements/BoxIcon';
import { COLOR_WHITE } from '@src/types/colors';
import LinkWrapper from '../LinkWrapper';
import { getCTA } from '@src/utils/url';
import { ButtonStandard } from '../Button';

type CommunityImageCard = CommunityImageCardFragment;

const CommunityImageCard: React.FC<CommunityImageCard> = ({
  imageGalleryCollection,
  heading,
  description,
  location,
  developmentType,
  primaryCta
}) => {
  const { document } = useWindow();
  const [modal, setModal] = useState<ModalState>({ open: false, initial: 0 });
  const router = useRouter();
  const { handleOpen, handleClose } = useModalOpenOrClose(setModal, 'gallery');

  useEffect(() => {
    if (document) {
      if (modal.open) {
        router.beforePopState(() => {
          setModal({ open: false, initial: 0 });
          return true;
        });
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
    }
  }, [document, modal.open, router]);

  return (
    <>
      <LinkWrapper {...getCTA(primaryCta)}>
        <div className={styles.communityImageCard}>
          <div className={styles.communityImageCardContent}>
            <div
              className={styles.previewImage}
              onClick={(e) => {
                // prevent default link wrapper behavior
                e.preventDefault();
                imageGalleryCollection?.items?.length &&
                  imageGalleryCollection?.items?.length > 1 &&
                  handleOpen(0);
              }}
            >
              {imageGalleryCollection?.items[0]?.__typename ===
                'MediaImage' && (
                <MediaImage {...imageGalleryCollection.items[0]} />
              )}
              {imageGalleryCollection?.items?.length &&
                imageGalleryCollection?.items?.length > 1 && (
                  <div className={styles.galleryBadge}>
                    <BoxIcon name="camera" color={COLOR_WHITE} size="xs" />
                    {imageGalleryCollection?.items?.length}
                  </div>
                )}
            </div>
            <div className={styles.communityImageCardContentHeader}>
              <Heading priority={3} fontStyle="h4">
                {heading}
              </Heading>
              {location && (
                <p className={styles.communityImageCardLocation}>{location}</p>
              )}
              {developmentType && (
                <p className={styles.communityImageCardDevelopmentType}>
                  {developmentType}
                </p>
              )}
            </div>
            <p className={styles.communityImageCardDescription}>
              {description}
            </p>
          </div>
          <div className={styles.ctaContainer}>
            {primaryCta && (
              <ButtonStandard boxIcon="right-arrow-alt" iconPosition="right">
                {primaryCta.label || 'Explore'}
              </ButtonStandard>
            )}
          </div>
        </div>
      </LinkWrapper>
      <Modal
        appElement={
          (typeof document !== 'undefined' &&
            document.getElementById('__next')) ||
          undefined
        }
        isOpen={modal.open}
        onRequestClose={() => handleClose()}
        overlayClassName={styles.galleryOverlay}
        className={styles.galleryContent}
        contentLabel={`Gallery`}
      >
        <GalleryModal
          gallery={
            (imageGalleryCollection?.items.map(
              (item) => item?.cloudinarySource[0]
            ) as CloudinarySource[]) ?? []
          }
          initial={0}
          onRequestClose={() => handleClose()}
        />
      </Modal>
    </>
  );
};

export default CommunityImageCard;
